import React from 'react';
import { App as AntApp } from 'antd';
import { initNotificationInstances } from '../../utils/notificationUtils';
import { initModalInstance } from '../../utils/modalUtils';

interface NotificationProviderProps {
  children: React.ReactNode;
}

/**
 * AppContextConsumer component
 *
 * This component uses the App.useApp() hook to get the notification, message, and modal instances
 * and initializes them for use in the notification and modal utilities.
 */
const AppContextConsumer: React.FC = () => {
  // Get the notification, message, and modal instances from the App component
  const { notification, message, modal } = AntApp.useApp();

  // Initialize the notification and modal utilities
  React.useEffect(() => {
    initNotificationInstances(notification, message);
    initModalInstance(modal);
  }, [notification, message, modal]);

  return null;
};

/**
 * NotificationProvider component
 *
 * This component wraps the application with Ant Design's App component
 * to provide a context for notifications, modals, and message components.
 * This resolves the warning: "Static function can not consume context like dynamic theme"
 */
const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  return (
    <AntApp>
      <AppContextConsumer />
      {children}
    </AntApp>
  );
};

export default NotificationProvider;
